<template>
  <div class="call-rider-page">
    <!-- Premium Header Section -->
    <div class="rider-glass-header">
      <v-container class="px-6 py-6">
        <div class="d-flex align-center">
          <div class="header-icon-box mr-4">
            <v-icon color="white" large>mdi-car-side</v-icon>
          </div>
          <div>
            <h1 class="header-title white--text mb-0">เรียกรถ</h1>
            <p class="header-subtitle white--text mb-0">บริการรับ-ส่งผู้โดยสารระดับพรีเมียม สะดวก ปลอดภัย ทันใจ</p>
          </div>
        </div>
      </v-container>
    </div>

    <v-container class="rider-main-container px-6 pb-12">

      <!-- Active Travel Banner -->
      <v-expand-transition>
        <v-card
          v-if="activeTravel && currentStep === 0"
          class="active-travel-banner mb-6 pa-4"
          color="orange darken-1"
          dark
          elevation="3"
          @click="resumeActiveTravel"
          style="cursor: pointer; border-radius: 14px"
        >
          <div class="d-flex align-center">
            <v-icon class="mr-3" size="28">mdi-car-clock</v-icon>
            <div class="grow">
              <div class="font-weight-bold text-body-1">คุณมีการเรียกรถที่ยังไม่เสร็จ</div>
              <div class="caption opacity-80">
                #TRIP-{{ activeTravel.travelNumber || activeTravel.id.slice(-6).toUpperCase() }} ·
                {{ activeTravelStatusLabel }}
              </div>
            </div>
            <v-icon>mdi-chevron-right</v-icon>
          </div>
        </v-card>
      </v-expand-transition>

      <!-- Premium Stepper Progress -->
      <div class="stepper-wrapper mb-8">
        <div v-for="(step, i) in steps" :key="'step-'+i" class="step-node-wrapper">
          <div 
            class="step-node"
            :class="{ 'active': currentStep === i, 'completed': currentStep > i }"
          >
            <div class="node-circle">
              <v-icon v-if="currentStep > i" color="white" small>mdi-check</v-icon>
              <span v-else>{{ i + 1 }}</span>
            </div>
            <span class="node-label">{{ step }}</span>
          </div>
          <div v-if="i < steps.length - 1" class="node-line" :class="{ 'filled': currentStep > i }"></div>
        </div>
      </div>

      <!-- Step 0: Set Route -->
      <div v-if="currentStep === 0" class="step-content animate-fade-in">
        <v-row>
          <v-col cols="12" md="5">
            <v-card class="location-card pa-6" elevation="2">
              <h3 class="section-title mb-6 d-flex align-center">
                <v-icon color="primary" class="mr-2">mdi-map-marker-path</v-icon>
                ระบุเส้นทางการเดินทาง
              </h3>
              
              <!-- Origin Input -->
              <div class="route-input-group mb-6">
                <div class="input-label d-flex align-center mb-2">
                  <div class="indicator-dot origin mr-2"></div>
                  <span>จุดรับ (ต้นทาง)</span>
                </div>
                <v-card outlined class="custom-input-box clickable pa-3 mb-2" @click="openPicker('origin')">
                  <div class="d-flex align-start w-100">
                    <v-icon color="success" class="mr-2 mt-1">mdi-record-circle-outline</v-icon>
                    <div class="grow" style="min-width:0; max-width:100%; overflow-wrap:anywhere">
                      <div class="input-text-display" :class="{ 'placeholder': !origin }">
                        {{ origin ? origin.formattedAddress : 'แตะเพื่อระบุจุดรับ...' }}
                      </div>
                      <div v-if="origin && originDetail" class="detail-line">
                        <v-icon size="12" color="#16a34a" class="mr-1">mdi-note-text-outline</v-icon>
                        <span>{{ originDetail }}</span>
                      </div>
                    </div>
                  </div>
                </v-card>
                <v-text-field
                  v-model="originDetail"
                  label="รายละเอียดเพิ่มเติม (เช่น รอหน้าตึก A, ใส่เสื้อสีแดง)"
                  dense
                  outlined
                  hide-details
                  class="mt-1"
                  color="primary"
                ></v-text-field>
              </div>

              <!-- Destination Input -->
              <div class="route-input-group">
                <div class="input-label d-flex align-center mb-2">
                  <div class="indicator-dot destination mr-2"></div>
                  <span>จุดส่ง (ปลายทาง)</span>
                </div>
                <v-card outlined class="custom-input-box clickable pa-3 mb-2" @click="openPicker('destination')">
                  <div class="d-flex align-start w-100">
                    <v-icon color="error" class="mr-2 mt-1">mdi-map-marker</v-icon>
                    <div class="grow" style="min-width:0; max-width:100%; overflow-wrap:anywhere">
                      <div class="input-text-display" :class="{ 'placeholder': !destination }">
                        {{ destination ? destination.formattedAddress : 'แตะเพื่อระบุจุดส่ง...' }}
                      </div>
                      <div v-if="destination && destinationDetail" class="detail-line">
                        <v-icon size="12" color="#dc2626" class="mr-1">mdi-note-text-outline</v-icon>
                        <span>{{ destinationDetail }}</span>
                      </div>
                    </div>
                  </div>
                </v-card>
                <v-text-field
                  v-model="destinationDetail"
                  label="รายละเอียดเพิ่มเติม (เช่น ตึก B ระบุชั้น)"
                  dense
                  outlined
                  hide-details
                  class="mt-1"
                  color="primary"
                ></v-text-field>
              </div>

              <!-- Distance Info -->
              <v-expand-transition>
                <v-card v-if="origin && destination" class="distance-summary-box mt-8 pa-4" flat color="#FFF8F0">
                  <div class="d-flex justify-space-between align-center mb-2">
                    <span class="grey--text text--darken-1">ระยะทางทั้งหมด</span>
                    <span class="font-weight-bold text-h6">{{ estimatedDistance }} กม.</span>
                  </div>
                  <div class="d-flex justify-space-between align-center">
                    <span class="grey--text text--darken-1">เวลาเดินทางโดยประมาณ</span>
                    <span class="font-weight-bold blue--text">{{ estimatedTime }} นาที</span>
                  </div>
                </v-card>
              </v-expand-transition>

              <v-btn
                block
                x-large
                color="primary"
                class="next-action-btn mt-8"
                :disabled="!origin || !destination"
                elevation="4"
                @click="currentStep = 1"
              >
                ดูราคาและเลือกประเภทรถ <v-icon right>mdi-arrow-right</v-icon>
              </v-btn>
            </v-card>
          </v-col>

          <v-col cols="12" md="7">
            <div class="preview-map-wrapper">
              <div class="preview-map-container" ref="previewMapEl">
                <div v-if="!origin || !destination" class="map-overlay-hint">
                  <v-icon color="white" size="50" class="mb-2">mdi-map-marker-distance</v-icon>
                  <p class="mb-0">ปักหมุดจุดรับ-ส่งบนแผนที่เพื่อดูเส้นทาง</p>
                </div>
              </div>
            </div>
          </v-col>
        </v-row>
      </div>

      <!-- Step 1: Select Rider Type -->
      <div v-if="currentStep === 1" class="step-content animate-fade-in">
        <h3 class="section-title mb-6 d-flex align-center">
          <v-icon color="primary" class="mr-2">mdi-account-group</v-icon>
          เลือกประเภทการเดินทาง
        </h3>
        <v-row>
          <v-col v-for="type in riderTypes" :key="type.id" cols="12" sm="6" md="4">
            <v-card
              class="rider-type-card-premium"
              :class="{ 'selected': selectedRiderType === type.id, 'rider-type-card-premium--disabled': isTypeUnavailable(type) }"
              flat
              outlined
              @click="!isTypeUnavailable(type) && (selectedRiderType = type.id)"
            >
              <div class="type-badge" v-if="type.bestValue && !isTypeUnavailable(type)">แนะนำ</div>
              <div class="type-badge type-badge--disabled" v-if="isTypeUnavailable(type)">ระยะทางเกิน</div>
              <div class="card-inner pa-6 text-center">
                <div class="type-icon-wrapper mb-4" :style="{ background: type.bgColor }">
                  <v-icon size="40" :color="type.color">{{ type.icon }}</v-icon>
                </div>
                <div class="text-h6 font-weight-bold mb-1">{{ type.name }}</div>
                <div class="text-caption grey--text mb-4">{{ type.desc }}</div>
                <div v-if="isTypeUnavailable(type)" class="text-caption red--text mb-2">
                  รับได้สูงสุด {{ type.serviceMaxDistance }} กม. (ระยะของคุณ {{ estimatedDistance }} กม.)
                </div>
                
                <v-divider class="mb-4"></v-divider>
                
                <div class="price-details mb-4">
                  <div class="d-flex justify-space-between align-center mb-1">
                    <span class="caption grey--text">ราคาเริ่มต้น</span>
                    <span class="caption font-weight-medium" v-if="type.pricingTiers && type.pricingTiers[0]">
                      ฿{{ type.pricingTiers[0].flatPrice || type.pricingTiers[0].pricePerKm }}
                    </span>
                    <span class="caption font-weight-medium" v-else>—</span>
                  </div>
                  <div class="d-flex justify-space-between align-center">
                    <span class="caption grey--text">ตรวจสอบราคา</span>
                    <span class="caption font-weight-medium">ตามระยะทาง {{ estimatedDistance }} กม.</span>
                  </div>
                </div>
                
                <div class="total-price-display">
                  <span class="price-unit">฿</span>
                  <span class="price-amount">{{ calcTotalPrice(type) }}</span>
                </div>
              </div>
            </v-card>
          </v-col>
        </v-row>

        <!-- Driver Note -->
        <v-card class="mt-8 pa-6 decoration-border" flat outlined color="white">
          <h4 class="font-weight-bold mb-4 d-flex align-center">
            <v-icon color="primary" class="mr-2">mdi-note-edit</v-icon>
            จุดสังเกต หรือ ข้อความถึงคนขับ (ถ้ามี)
          </h4>
          <v-textarea
            v-model="parcelNote"
            placeholder="เช่น ใส่เสื้อสีแดงยืนรอหน้าเซเว่น หรือ มีกระเป๋าเดินทาง 1 ใบ ฯลฯ"
            outlined
            dense
            rows="2"
            hide-details
            color="primary"
            class="white"
          ></v-textarea>
        </v-card>

        <div class="d-flex justify-space-between mt-10">
          <v-btn text large @click="currentStep = 0" class="px-8 font-weight-bold">
            <v-icon left>mdi-arrow-left</v-icon> ย้อนกลับ
          </v-btn>
          <v-btn 
            color="primary" 
            large 
            class="px-12 next-action-btn" 
            elevation="4"
            :disabled="!selectedRiderType"
            @click="currentStep = 2"
          >
            ต่อไป ชำระเงิน <v-icon right>mdi-arrow-right</v-icon>
          </v-btn>
        </div>
      </div>

      <!-- Step 2: Payment & Final Confirm -->
      <div v-if="currentStep === 2" class="step-content animate-fade-in">
        <v-row>
          <v-col cols="12" md="7">
            <h3 class="section-title mb-6">วิธีชำระเงิน</h3>
            <v-card flat outlined class="mb-6 overflow-hidden payment-card">
              <v-list class="pa-0">
                <v-list-item-group v-model="paymentMethod" mandatory color="primary">
                  <v-list-item value="cash" class="py-4 px-6 border-bottom">
                    <template v-slot:default="{ active }">
                      <v-list-item-icon class="mr-4">
                        <v-icon :color="active ? 'primary' : ''" size="36">mdi-cash-multiple</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-bold">ชำระด้วยเงินสด</v-list-item-title>
                        <v-list-item-subtitle>ชำระยอดกับคนขับเมื่อถึงที่หมาย</v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <div class="custom-selection-dot" :class="{ 'active': active }">
                          <div class="dot-inner"></div>
                        </div>
                      </v-list-item-action>
                    </template>
                  </v-list-item>
                  
                  <v-list-item value="qr" class="py-4 px-6 border-bottom">
                    <template v-slot:default="{ active }">
                      <v-list-item-icon class="mr-4">
                        <v-icon :color="active ? 'primary' : ''" size="36">mdi-qrcode-scan</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-bold">Thai QR / พร้อมเพย์</v-list-item-title>
                        <v-list-item-subtitle>สแกนจ่ายทันทีผ่านแอปธนาคาร</v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <div class="custom-selection-dot" :class="{ 'active': active }">
                          <div class="dot-inner"></div>
                        </div>
                      </v-list-item-action>
                    </template>
                  </v-list-item>

                  <v-list-item value="wallet" class="py-4 px-6">
                    <template v-slot:default="{ active }">
                      <v-list-item-icon class="mr-4">
                        <v-icon :color="active ? 'primary' : ''" size="36">mdi-wallet-filled</v-icon>
                      </v-list-item-icon>
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-bold">ELSON Wallet</v-list-item-title>
                        <v-list-item-subtitle>
                          ยอดคงเหลือ:
                          <span :class="walletBalance >= finalTotalPrice ? 'primary--text' : 'error--text'" class="font-weight-bold">
                            ฿{{ walletBalance.toLocaleString('th-TH', { minimumFractionDigits: 2 }) }}
                          </span>
                          <span v-if="walletBalance < finalTotalPrice" class="error--text ml-1">(ไม่เพียงพอ)</span>
                        </v-list-item-subtitle>
                      </v-list-item-content>
                      <v-list-item-action>
                        <div class="custom-selection-dot" :class="{ 'active': active }">
                          <div class="dot-inner"></div>
                        </div>
                      </v-list-item-action>
                    </template>
                  </v-list-item>
                </v-list-item-group>
              </v-list>
            </v-card>
          </v-col>

          <v-col cols="12" md="5">
            <h3 class="section-title mb-6">สรุปรายการการเดินทาง</h3>
            <v-card flat outlined class="pa-6 sticky-summary-card">
              <!-- Route Summary -->
              <div class="summary-route-path mb-8">
                <div class="path-node">
                  <div class="node-icon origin"></div>
                  <div class="node-body">
                    <div class="label">จุดรับผู้โดยสาร</div>
                    <div class="value">{{ origin ? origin.addressDetail : '—' }}</div>
                    <div v-if="originDetail" class="summary-note">
                      <v-icon size="12" color="#16a34a" class="mr-1">mdi-note-text-outline</v-icon>
                      <span>{{ originDetail }}</span>
                    </div>
                  </div>
                </div>
                <div class="path-connector"></div>
                <div class="path-node mt-4">
                  <div class="node-icon destination"></div>
                  <div class="node-body">
                    <div class="label">จุดส่งผู้โดยสาร</div>
                    <div class="value">{{ destination ? destination.addressDetail : '—' }}</div>
                    <div v-if="destinationDetail" class="summary-note">
                      <v-icon size="12" color="#dc2626" class="mr-1">mdi-note-text-outline</v-icon>
                      <span>{{ destinationDetail }}</span>
                    </div>
                  </div>
                </div>
              </div>

              <!-- Rider Summary -->
              <v-card flat color="#f1f5f9" class="pa-4 rounded-lg mb-6">
                <div class="d-flex align-center">
                  <v-avatar color="white" size="44" class="mr-3">
                    <v-icon color="primary">{{ currentRiderTypeObj.icon }}</v-icon>
                  </v-avatar>
                  <div class="grow">
                    <div class="font-weight-bold">{{ currentRiderTypeObj.name }}</div>
                    <div class="caption grey--text">{{ estimatedDistance }} กม. | {{ estimatedTime }} นาที</div>
                  </div>
                  <div class="text-right font-weight-bold">฿{{ finalTotalPrice }}</div>
                </div>
              </v-card>

              <div class="price-breakdown mb-6">
                <div class="d-flex justify-space-between mb-2">
                  <span class="grey--text">จุดสังเกต</span>
                  <span class="font-weight-medium text-truncate" style="max-width: 150px">{{ parcelNote || 'ไม่ระบุ' }}</span>
                </div>
                <v-divider class="my-4"></v-divider>
                <div class="d-flex justify-space-between align-center">
                  <span class="text-h6 font-weight-bold">ยอดที่ต้องชำระ</span>
                  <span class="text-h4 font-weight-black primary--text">฿{{ finalTotalPrice }}</span>
                </div>
              </div>

              <v-alert v-if="errorMessage" type="error" dense class="mb-4 text-body-2">
                {{ errorMessage }}
              </v-alert>

              <v-btn
                block
                x-large
                color="primary"
                class="next-action-btn py-6"
                elevation="8"
                :loading="processing"
                :disabled="paymentMethod === 'wallet' && walletBalance < finalTotalPrice"
                @click="confirmOrder"
              >
                ยืนยันเพื่อเรียกรถ
              </v-btn>
            </v-card>
          </v-col>
        </v-row>
        
        <v-btn text @click="currentStep = 1" class="mt-4 px-0 font-weight-bold">
          <v-icon left>mdi-arrow-left</v-icon> กลับไปเลือกประเภทรถ
        </v-btn>
      </div>

      <!-- Step 3: Result Screen -->
      <div v-if="currentStep === 3" class="step-content animate-fade-in">
        <div v-if="!riderFound" class="searching-screen">
          <!-- Map hero -->
          <div class="nearby-map-wrap">
            <div ref="nearbyMapEl" class="nearby-map-el"></div>
            <div class="map-interaction-blocker"></div>

            <!-- Top bar inside map -->
            <div class="map-top-bar">
              <div class="map-pulse-dot"></div>
              <span>กำลังค้นหาในรัศมี 2 กม.</span>
            </div>

            <!-- Rider count badge -->
            <div class="nearby-map-badge">
              <span class="badge-count">{{ nearbyRiderCount }}</span>
              <span class="badge-label">คนขับในรัศมี</span>
            </div>
          </div>

          <!-- Info card below map -->
          <div class="searching-info-card">
            <div class="searching-title-row">
              <div class="searching-spinner">
                <div class="spinner-ring"></div>
                <v-icon color="white" size="20">mdi-motorbike</v-icon>
              </div>
              <div>
                <div class="searching-title">กำลังหาคนขับใกล้คุณ</div>
                <div class="searching-sub">ระบบกำลังจับคู่กับไรเดอร์ที่พร้อมรับงาน</div>
              </div>
            </div>

            <v-progress-linear indeterminate color="#F4811F" rounded height="4" class="mt-4 mb-1" background-color="rgba(244,129,31,0.15)"></v-progress-linear>

            <div class="searching-footer">
              <div class="order-ref-chip" v-if="orderNo">
                <v-icon size="14" color="#64748b" class="mr-1">mdi-identifier</v-icon>
                {{ orderNo }}
              </div>
              <v-btn small text color="error" :loading="cancelling" @click="cancelActiveTravel" class="cancel-btn">
                <v-icon left x-small>mdi-close-circle-outline</v-icon>
                ยกเลิก
              </v-btn>
            </div>
          </div>
        </div>
        
        <div v-else class="success-screen-premium">
          <div class="success-center-wrap mx-auto">

            <!-- Status Header -->
            <div class="success-status-bar mb-5">
              <div class="success-check-circle mr-3">
                <v-icon color="white" size="28">mdi-check-bold</v-icon>
              </div>
              <div>
                <div class="text-subtitle-1 font-weight-black white--text" style="line-height:1.2;">ไรเดอร์รับงานแล้ว!</div>
                <div class="caption white--text" style="opacity:0.75;">กำลังเดินทางมารับคุณ</div>
              </div>
              <v-spacer></v-spacer>
              <div class="trip-ref-badge">#{{ orderNo }}</div>
            </div>

            <!-- Rider Card -->
            <v-card class="success-card mb-4 pa-5" elevation="0">
              <div class="d-flex align-center">
                <v-avatar size="60" color="#FFF5EC" class="mr-4" style="border:2.5px solid #F4811F;flex-shrink:0;">
                  <v-img v-if="riderAvatarUrl" :src="riderAvatarUrl" />
                  <v-icon v-else size="32" color="#F4811F">mdi-account</v-icon>
                </v-avatar>
                <div style="min-width:0;flex:1;">
                  <div class="text-subtitle-1 font-weight-black">{{ [assignedRider.natId_firstName, assignedRider.natId_lastName].filter(Boolean).join(' ') || assignedRider.username || 'ไรเดอร์' }}</div>
                  <div class="d-flex align-center" v-if="riderReviewAvg > 0">
                    <v-icon size="13" color="#F59E0B">mdi-star</v-icon>
                    <span class="caption font-weight-bold ml-1" style="color:#B45309;">{{ riderReviewAvg.toFixed(1) }}</span>
                    <span class="caption grey--text ml-1">({{ riderReviewCount }} รีวิว)</span>
                  </div>
                  <div class="caption grey--text d-flex align-center flex-wrap" style="gap:6px;">
                    <span><v-icon size="12">{{ currentRiderTypeObj?.icon || 'mdi-motorbike' }}</v-icon> {{ currentRiderTypeObj?.name || '—' }}</span>
                    <span v-if="assignedRider.phone"><v-icon size="12">mdi-phone</v-icon> {{ assignedRider.phone }}</span>
                  </div>
                </div>
              </div>
            </v-card>

            <!-- Route + Stats Card -->
            <v-card class="success-card mb-4 pa-5" elevation="0">
              <!-- Route -->
              <div class="d-flex align-start mb-2">
                <div class="route-dot origin mr-3" style="margin-top:5px;flex-shrink:0;"></div>
                <div style="min-width:0;">
                  <div class="caption grey--text">จุดรับ</div>
                  <div class="text-body-2 font-weight-bold text-truncate">{{ origin?.addressDetail || origin?.formattedAddress || '—' }}</div>
                </div>
              </div>
              <div class="route-dashed-line mb-2" style="margin-left:5px;"></div>
              <div class="d-flex align-start mb-4">
                <div class="route-dot destination mr-3" style="margin-top:5px;flex-shrink:0;"></div>
                <div style="min-width:0;">
                  <div class="caption grey--text">จุดส่ง</div>
                  <div class="text-body-2 font-weight-bold text-truncate">{{ destination?.addressDetail || destination?.formattedAddress || '—' }}</div>
                </div>
              </div>
              <!-- Stats row -->
              <div class="stats-row">
                <div class="stat-item">
                  <div class="stat-value">{{ estimatedDistance }}</div>
                  <div class="stat-label">กม.</div>
                </div>
                <div class="stat-divider"></div>
                <div class="stat-item">
                  <div class="stat-value">{{ estimatedTime }}</div>
                  <div class="stat-label">นาที</div>
                </div>
                <div class="stat-divider"></div>
                <div class="stat-item">
                  <div class="stat-value" style="color:#F4811F;">฿{{ finalTotalPrice }}</div>
                  <div class="stat-label">ราคา</div>
                </div>
              </div>
            </v-card>

            <!-- Actions -->
            <div class="d-flex mb-4" style="gap:10px;">
              <v-btn outlined color="#16a34a" height="48" class="flex-grow-1 rounded-xl font-weight-bold"
                :disabled="!assignedRider.phone" @click="callAssignedRider">
                <v-icon left small>mdi-phone</v-icon> โทร
              </v-btn>
              <v-btn outlined color="#3B82F6" height="48" class="flex-grow-1 rounded-xl font-weight-bold"
                :disabled="!assignedRider.id" @click="chatAssignedRider">
                <v-icon left small>mdi-chat-processing-outline</v-icon> แชท
              </v-btn>
              <v-btn color="#F4811F" dark height="48" class="flex-grow-1 rounded-xl font-weight-bold" @click="goOrderHistory">
                <v-icon left small>mdi-map-marker-path</v-icon> ติดตาม
              </v-btn>
            </div>

            <v-btn block large color="#0f172a" dark class="rounded-xl font-weight-bold mb-3" height="52" @click="goOrderHistory">
              ดูรายละเอียดการเดินทาง <v-icon right small>mdi-arrow-right</v-icon>
            </v-btn>
            <div class="text-center">
              <v-btn text small @click="resetForm" color="grey">เรียกรถใหม่</v-btn>
            </div>

          </div>
        </div>
      </div>
    </v-container>

    <!-- Premium Fullscreen Map Picker Dialog -->
    <v-dialog v-model="pickerDialog" fullscreen transition="dialog-bottom-transition" persistent>
      <v-card class="map-dialog-container">
        <!-- Floating Header -->
        <div class="map-floating-header px-4 pt-4">
          <div class="d-flex align-center">
            <v-btn fab small color="white" elevation="3" @click="pickerDialog = false">
              <v-icon color="black">mdi-arrow-left</v-icon>
            </v-btn>
            <div class="grow ml-4">
              <v-card elevation="3" class="rounded-xl px-6 py-2 d-flex align-center bg-white" style="max-width: fit-content;">
                <v-icon :color="pickerMode === 'origin' ? 'success' : 'error'" class="mr-3">
                  {{ pickerMode === 'origin' ? 'mdi-record-circle' : 'mdi-map-marker' }}
                </v-icon>
                <div class="font-weight-bold text-subtitle-1">
                  {{ pickerMode === 'origin' ? 'ระบุจุดรับ' : 'ระบุจุดส่ง' }}
                </div>
              </v-card>
            </div>
          </div>
        </div>
        
        <div class="map-fullscreen-wrapper">
          <GoogleMapPicker 
            ref="mainPicker"
            v-model="tempCoords"
            :sync-address-fields="true"
            height="100%"
            :show-coords="false"
            :show-reset="false"
            class="premium-google-picker"
            @address-resolved="onAddressResolved"
          />
        </div>
        
        <!-- Bottom Information Panel -->
        <div class="map-bottom-panel">
          <v-card class="pa-6 pb-8" elevation="16" color="white" style="border-radius: 36px 36px 0 0 !important;">
            <div class="d-flex align-start mb-4">
              <v-avatar :color="pickerMode === 'origin' ? 'success lighten-1' : 'error lighten-1'" size="44" class="mr-4 mt-1 shadow-sm">
                <v-icon color="white">
                  {{ pickerMode === 'origin' ? 'mdi-store-marker-outline' : 'mdi-map-marker-outline' }}
                </v-icon>
              </v-avatar>
              <div class="grow text-truncate border-bottom pb-3">
                <div class="caption font-weight-bold grey--text text-uppercase mb-1" style="letter-spacing: 0.5px">
                  {{ pickerMode === 'origin' ? 'ระบุจุดรับ (ต้นทาง)' : 'ระบุจุดส่ง (ปลายทาง)' }}
                </div>
                <div class="text-subtitle-1 font-weight-bold text-truncate black--text" style="line-height:1.2">
                  {{ tempAddress.formattedAddress || 'กำลังค้นหาตำแหน่ง...' }}
                </div>
              </div>
            </div>
            <v-btn 
              color="primary" 
              x-large 
              block 
              class="rounded-xl font-weight-bold py-6 text-h6 mt-2 shadow-md" 
              elevation="6" 
              @click="confirmPicker" 
              :disabled="!tempAddress.formattedAddress"
            >
              ยืนยันตำแหน่งนี้ <v-icon right>mdi-check-circle-outline</v-icon>
            </v-btn>
          </v-card>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
/* global google */
import GoogleMapPicker from "@/components/Shared/GoogleMapPicker.vue";
import { loadGoogleMapsScript } from "@/utils/googleMapsLoader";
import { getRiderServiceConfigsApi } from "@/services/api/riderServices";
import { getRiderReviewsApi } from "@/services/api/review";
import { getNearbyRiders } from "@/services/api/geography";
import {
  createTravelApi,
  payTravelByWalletApi,
  cancelTravelApi,
  getMyTravelsApi,
  getTravelByIdApi,
  startTravelSeekingCashApi,
} from "@/services/api/travel";
import WalletService from "@/services/api/wallet";

export default {
  name: "CallRider",
  components: {
    GoogleMapPicker
  },
  data() {
    return {
      currentStep: 0,
      steps: ["เส้นทาง", "ประเภทรถ", "ชำระเงิน", "ยืนยัน"],

      // Route Data
      origin: null,
      originDetail: "",
      destination: null,
      destinationDetail: "",
      mapCenter: { lat: 13.7563, lng: 100.5018 },

      // Preview Map State
      previewMap: null,
      directionsService: null,
      directionsRenderer: null,

      // Picker
      pickerDialog: false,
      pickerMode: 'origin',
      tempCoords: { lat: 13.7563, lng: 100.5018 },
      tempAddress: {},

      // Rider Selection
      selectedRiderType: null,
      riderTypes: [],

      parcelNote: "",

      // Payment
      paymentMethod: "cash",
      walletBalance: 0,
      processing: false,
      errorMessage: "",

      // Created travel
      currentTravelId: null,
      currentTravelMeta: { vehicleTypeId: null, serviceTypeId: null },

      // Active travel banner (travel ที่ยังไม่เสร็จ)
      activeTravel: null,
      loadingActiveTravel: false,

      // Result — ไรเดอร์จริงจาก API / socket (ไม่ใช้ setTimeout จำลอง)
      riderFound: false,
      assignedRider: null,
      currentTravel: null,
      riderReviewAvg: 0,
      riderReviewCount: 0,
      orderNo: "",
      travelPollTimer: null,
      travelRiderAcceptedHandler: null,
      cancelling: false,
      googleDistance: 0,
      googleTime: 0,

      // Nearby-riders map (step 3)
      nearbyMap: null,
      nearbyRiderMarkers: [],
      nearbyOriginMarker: null,
      nearbyRadiusCircle: null,
      nearbyRiderCount: 0,
      nearbyPollTimer: null,
    };
  },
  computed: {
    estimatedDistance() {
      if (this.googleDistance > 0) return this.googleDistance;
      if (!this.origin || !this.destination) return 0;
      // Formula for calculating distance between coordinates (Haversine)
      const lat1 = this.origin.lat;
      const lon1 = this.origin.lng;
      const lat2 = this.destination.lat;
      const lon2 = this.destination.lng;
      
      const R = 6371; // km
      const dLat = (lat2 - lat1) * Math.PI / 180;
      const dLon = (lon2 - lon1) * Math.PI / 180;
      const a = 
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) * 
        Math.sin(dLon / 2) * Math.sin(dLon / 2); 
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a)); 
      const d = R * c;
      
      return Math.round(d * 1.25 * 10) / 10; // 1.25 multiplier for road winding
    },
    estimatedTime() {
      if (this.googleTime > 0) return this.googleTime;
      if (!this.origin || !this.destination) return 0;
      return Math.max(5, Math.round(this.estimatedDistance * 3.5)); // ~3.5 min/km
    },
    currentRiderTypeObj() {
      return this.riderTypes.find(t => t.id === this.selectedRiderType) || this.riderTypes[0];
    },
    finalTotalPrice() {
      if (this._resumedPrice) return this._resumedPrice;
      return this.calcTotalPrice(this.currentRiderTypeObj);
    },
    activeTravelStatusLabel() {
      const map = {
        pending:        "รอชำระเงิน",
        seeking_rider:  "กำลังหาไรเดอร์...",
        rider_accepted: "ไรเดอร์รับงานแล้ว กำลังมารับ",
        in_progress:    "กำลังเดินทาง",
      };
      return map[this.activeTravel?.status] || this.activeTravel?.status || "";
    },
    riderAvatarUrl() {
      const r = this.assignedRider;
      if (!r) return "";
      if (r.profileImage) {
        const raw = String(r.profileImage).trim();
        if (/^https?:\/\//i.test(raw) || raw.startsWith("data:")) return raw;
        const base = (process.env.VUE_APP_API_URL || "").replace(/\/$/, "");
        const path = raw.startsWith("/") ? raw : "/" + raw;
        return base + path;
      }
      const name = encodeURIComponent(r.username || "Rider");
      return `https://ui-avatars.com/api/?name=${name}&background=F4811F&color=fff&size=128`;
    },
  },
  watch: {
    origin() {
      this.googleDistance = 0;
      this.googleTime = 0;
      this.renderRoute();
    },
    destination() {
      this.googleDistance = 0;
      this.googleTime = 0;
      this.renderRoute();
    },
    estimatedDistance() {
      const sel = this.riderTypes.find(t => t.id === this.selectedRiderType);
      if (sel && this.isTypeUnavailable(sel)) {
        const next = this.riderTypes.find(t => !this.isTypeUnavailable(t));
        this.selectedRiderType = next ? next.id : null;
      }
    },
    currentStep(val) {
      if (val === 3 && !this.riderFound) {
        this.$nextTick(() => this.initNearbyMap());
      } else if (val !== 3) {
        this.destroyNearbyMap();
      }
      if (val === 0) {
        // กลับมา step แรก → re-init preview map (DOM element ใหม่หลังจาก v-if เปลี่ยน)
        this.$nextTick(() => {
          this.initPreviewMap();
          if (this.origin && this.destination) this.renderRoute();
        });
      }
    },
    riderFound(val) {
      if (val) this.destroyNearbyMap();
    },
  },
  mounted() {
    this.loadGoogleMapApi();
    this.fetchRiderServices();
    this.fetchWalletBalance();
    this.fetchActiveTravel();
  },
  methods: {
    async fetchRiderServices() {
      try {
        const data = await getRiderServiceConfigsApi({
          isActive: true,
          serviceCode: 'PASSENGER',
        });

        if (data && data.success) {
          const configs = data.data;
          
          this.riderTypes = configs.map((c, index) => {
             const vtName = c.vehicleTypeRef?.name || '';
             const vt = String(c.vehicleType || '');
             const isMoto = vt.includes('MOTO') || /จักรยานยนต์|มอเตอร์ไซค์/.test(vtName);
             const isBike = !isMoto && (/BIKE|BICYCLE/i.test(vt) || /^(จักยาน|จักรยาน|รถจักรยาน)$/.test(vtName.trim()) || /จักรยาน(?!ยนต์)/.test(vtName));
             const tiers = c.pricingTiers || [];
             // ระยะทางสูงสุดที่ service นี้รับได้ มาจาก config.maxServiceDistance (null = ไม่จำกัด)
             const serviceMaxDistance = (c.maxServiceDistance == null) ? Infinity : c.maxServiceDistance;
             let icon = 'mdi-car-side';
             let color = '#2196F3';
             let bgColor = '#E3F2FD';
             let desc = 'นั่งสบาย กันแดดกันฝน';
             if (isMoto) {
               icon = 'mdi-motorbike'; color = '#F4811F'; bgColor = '#FFF5EC';
               desc = 'เดินทางรวดเร็ว ซอกแซกง่าย';
             } else if (isBike) {
               icon = 'mdi-bike'; color = '#10B981'; bgColor = '#E8F5E9';
               desc = 'ประหยัด เป็นมิตรกับสิ่งแวดล้อม';
             }
             return {
                id: c.id,
                configId: c.id,
                vehicleTypeId: c.vehicleTypeId,
                serviceTypeId: c.serviceTypeId,
                name: vtName,
                desc: c.description || desc,
                icon,
                color,
                bgColor,
                bestValue: index === 0,
                pricingTiers: tiers,
                serviceMaxDistance,
             };
          });
          
          // default = motorcycle ถ้ามี ไม่งั้น fallback รายการแรก
          if (this.riderTypes.length > 0 && !this.selectedRiderType) {
            const firstAvailable = this.riderTypes.find(t => !this.isTypeUnavailable(t));
            const moto = this.riderTypes.find(t => t.icon === 'mdi-motorbike' && !this.isTypeUnavailable(t));
            this.selectedRiderType = (moto || firstAvailable || this.riderTypes[0]).id;
          }
        }
      } catch (error) {
        console.error("Failed to fetch rider configs:", error);
      }
    },
    async loadGoogleMapApi() {
      try {
        await loadGoogleMapsScript();
        this.initPreviewMap();
      } catch (e) {
        console.warn(e);
      }
    },
    initPreviewMap() {
       if (!window.google || !window.google.maps) return;
       // Setup non-interactive map
       this.previewMap = new google.maps.Map(this.$refs.previewMapEl, {
         center: this.mapCenter,
         zoom: 12,
         disableDefaultUI: true,
         backgroundColor: '#eef2f6',
         gestureHandling: 'none' // Prevent all scrolling / dragging inside preview
       });
       this.directionsService = new google.maps.DirectionsService();
       this.directionsRenderer = new google.maps.DirectionsRenderer({
         map: this.previewMap,
         suppressMarkers: false,
         polylineOptions: { strokeColor: '#F4811F', strokeWeight: 5, strokeOpacity: 0.8 }
       });
       this.trySetOriginFromUserLocation();
    },
    applyGeolocationAsOrigin(lat, lng, formattedAddress, addressDetail) {
      if (this.origin) return;
      const fa = (formattedAddress || "").trim();
      const ad = (addressDetail || "").trim();
      const fb = this.formatCoordsFallbackLabel(lat, lng);
      this.origin = {
        lat,
        lng,
        formattedAddress: fa || fb,
        addressDetail: ad || fa.split(",")[0].trim() || fb,
      };
      this.mapCenter = { lat, lng };
      if (this.previewMap) {
        this.previewMap.setCenter({ lat, lng });
      }
      this.$nextTick(() => this.renderRoute());
    },
    formatCoordsFallbackLabel(lat, lng) {
      return `ตำแหน่งปัจจุบัน (${lat.toFixed(5)}, ${lng.toFixed(5)})`;
    },
    reverseGeocodeAndSetOrigin(lat, lng) {
      if (this.origin || !window.google?.maps?.Geocoder) return;

      const finishWithFallback = () => {
        if (this.origin) return;
        const fb = this.formatCoordsFallbackLabel(lat, lng);
        this.applyGeolocationAsOrigin(lat, lng, fb, fb);
      };

      const geocoder = new google.maps.Geocoder();
      geocoder.geocode(
        { location: { lat, lng }, language: "th", region: "th" },
        (results, status) => {
          if (this.origin) return;
          if (status === "OK" && results?.[0]?.formatted_address) {
            const formatted = results[0].formatted_address;
            this.applyGeolocationAsOrigin(
              lat,
              lng,
              formatted,
              formatted.split(",")[0].trim() || formatted
            );
            return;
          }
          finishWithFallback();
        }
      );
    },
    trySetOriginFromUserLocation() {
      if (!navigator.geolocation || this.origin) return;
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          const lat = pos.coords.latitude;
          const lng = pos.coords.longitude;
          this.reverseGeocodeAndSetOrigin(lat, lng);
        },
        () => {},
        { enableHighAccuracy: true, timeout: 15000, maximumAge: 30000 }
      );
    },
    renderRoute() {
       if (!this.directionsService || !this.directionsRenderer || !this.origin || !this.destination) return;
       
       const originLatLng = new google.maps.LatLng(this.origin.lat, this.origin.lng);
       const destLatLng = new google.maps.LatLng(this.destination.lat, this.destination.lng);
       
       this.directionsService.route({
          origin: originLatLng,
          destination: destLatLng,
          travelMode: 'DRIVING'
       }, (response, status) => {
          if (status === 'OK') {
             this.directionsRenderer.setDirections(response);
             const leg = response.routes[0].legs[0];
             if (leg) {
               this.googleDistance = Math.round((leg.distance.value / 1000) * 10) / 10;
               this.googleTime = Math.ceil(leg.duration.value / 60);
             }
          } else {
             // Fallback: draw straight bounds if real route fails (e.g. no roads)
             const bounds = new google.maps.LatLngBounds();
             bounds.extend(originLatLng);
             bounds.extend(destLatLng);
             if (this.previewMap) this.previewMap.fitBounds(bounds);
          }
       });
    },
    openPicker(mode) {
      this.pickerMode = mode;
      const current = mode === 'origin' ? this.origin : this.destination;
      if (current) {
        this.tempCoords = { lat: current.lat, lng: current.lng };
        this.tempAddress = { ...current };
      } else {
        this.tempCoords = { ...this.mapCenter };
        this.tempAddress = {};
      }
      this.pickerDialog = true;
    },
    onAddressResolved(address) {
      if (!address.formattedAddress) return;
      this.tempAddress = {
        ...address,
        lat: this.tempCoords.lat,
        lng: this.tempCoords.lng
      };
    },
    confirmPicker() {
      if (this.pickerMode === 'origin') {
        this.origin = { ...this.tempAddress };
        this.mapCenter = { lat: this.origin.lat, lng: this.origin.lng };
      } else {
        this.destination = { ...this.tempAddress };
      }
      this.pickerDialog = false;
    },
    isTypeUnavailable(type) {
      if (!type) return false;
      if (!this.estimatedDistance) return false;
      if (type.serviceMaxDistance == null || type.serviceMaxDistance === Infinity) return false;
      return this.estimatedDistance > type.serviceMaxDistance;
    },
    calcTotalPrice(type) {
      if (!this.estimatedDistance || !type) return 0;
      let totalPrice = 0;
      let remainingDistance = this.estimatedDistance;

      for (const tier of (type.pricingTiers || [])) {
        if (remainingDistance <= 0) break;

        const tierStart = tier.minDistance;
        const tierEnd = tier.maxDistance;
        const tierRange = tierEnd ? tierEnd - tierStart : remainingDistance;
        const applicableDistance = Math.min(remainingDistance, tierRange);

        if (tier.tierType === "FLAT") {
          totalPrice += tier.flatPrice || 0;
        } else {
          totalPrice += applicableDistance * (tier.pricePerKm || 0);
        }

        remainingDistance -= applicableDistance;
      }

      return Math.round(totalPrice * 100) / 100;
    },
    async fetchActiveTravel() {
      this.loadingActiveTravel = true;
      try {
        const res = await getMyTravelsApi();
        const travels = Array.isArray(res?.data) ? res.data : [];
        const active = travels.find(t => ["pending", "seeking_rider", "rider_accepted", "in_progress"].includes(t.status));
        this.activeTravel = active || null;
      } catch {
        this.activeTravel = null;
      } finally {
        this.loadingActiveTravel = false;
      }
    },

    resumeActiveTravel() {
      if (!this.activeTravel) return;
      const t = this.activeTravel;
      this.currentTravelId = t.id;
      this.currentTravelMeta = {
        vehicleTypeId: t.vehicleTypeId || null,
        serviceTypeId: t.serviceTypeId || null,
      };
      this.orderNo = t.travelNumber || `TRIP-${t.id.slice(-6).toUpperCase()}`;
      this.assignedRider = t.rider || null;
      this.currentTravel = t;

      // Restore route data so stats display correctly
      const originAddr = t.addressSnapshotNow || t.placeName || t.originAddress;
      const destAddr   = t.addressSnapshotStart || t.placeNameNow || t.destinationAddress;
      if (originAddr) {
        this.origin = {
          lat: t.latNow || t.originLat || 0,
          lng: t.lngNow || t.originLng || 0,
          formattedAddress: originAddr,
          addressDetail: originAddr,
        };
      }
      if (destAddr) {
        this.destination = {
          lat: t.latDest || t.destinationLat || 0,
          lng: t.lngDest || t.destinationLng || 0,
          formattedAddress: destAddr,
          addressDetail: destAddr,
        };
      }
      if (t.distance)              this.googleDistance = t.distance;
      if (t.tripDurationMinutes)   this.googleTime = t.tripDurationMinutes;
      else if (t.estimatedDurationMinutes) this.googleTime = t.estimatedDurationMinutes;
      if (t.price)                 this._resumedPrice = t.price;

      this.riderFound = !!(t.rider && ["rider_accepted", "in_progress", "completed"].includes(t.status));
      this.currentStep = 3;
      this.activeTravel = null;

      if (this.riderFound && t.rider?.id) {
        this.loadRiderReviews(t.rider.id);
      } else if (this.currentTravelId) {
        this.$nextTick(() => this.startTravelWatch(this.currentTravelId));
      }
    },

    async fetchWalletBalance() {
      try {
        const res = await WalletService.getMyWallet();
        this.walletBalance = res.data?.topUp?.balance ?? 0;
      } catch {
        this.walletBalance = 0;
      }
    },

    async confirmOrder() {
      this.processing = true;
      this.errorMessage = "";

      try {
        const selectedConfig = this.riderTypes.find(t => t.id === this.selectedRiderType);
        if (!selectedConfig) throw new Error("กรุณาเลือกประเภทรถ");

        // 1. Create travel
        const createRes = await createTravelApi({
          serviceConfigId: selectedConfig.configId,
          originLat: this.origin.lat,
          originLng: this.origin.lng,
          originAddress: this.origin.formattedAddress,
          destinationLat: this.destination.lat,
          destinationLng: this.destination.lng,
          destinationAddress: this.destination.formattedAddress,
          distanceKm: this.estimatedDistance,
          tripDurationMinutes: this.googleTime || null,
          note: this.parcelNote,
          originDetail: this.originDetail,
          destinationDetail: this.destinationDetail,
        });

        const travelId = createRes.data.id;
        this.currentTravelId = travelId;
        this.currentTravelMeta = {
          vehicleTypeId: createRes.data.vehicleTypeId || null,
          serviceTypeId: createRes.data.serviceTypeId || null,
        };

        // 2. Pay
        if (this.paymentMethod === "wallet") {
          await payTravelByWalletApi(travelId);
        } else if (this.paymentMethod === "qr") {
          this.$router.push({
            name: "travel-qr-payment",
            params: { travelId, total: this.finalTotalPrice },
          });
          return;
        } else if (this.paymentMethod === "cash") {
          await startTravelSeekingCashApi(travelId);
        }

        this.orderNo = createRes.data.travelNumber || `TRIP-${travelId.slice(-6).toUpperCase()}`;
        this.currentStep = 3;
        this.assignedRider = null;
        this.riderFound = false;

        localStorage.setItem("activeTravel", JSON.stringify({
          travelId,
          orderNo: this.orderNo,
          status: "seeking_rider",
        }));

        this.startTravelWatch(travelId);
      } catch (error) {
        this.errorMessage = error?.response?.data?.message || error.message || "เกิดข้อผิดพลาด";
      } finally {
        this.processing = false;
      }
    },
    goOrderHistory() {
      if (this.currentTravelId) {
        this.$router.push({ name: "travel_detail", params: { id: this.currentTravelId } });
      } else {
        this.$router.push({ name: "profile_user", query: { tab: "travel_history" } });
      }
    },
    chatAssignedRider() {
      const riderId = this.assignedRider?.id;
      if (!riderId) return;
      this.$root.$emit("open-floating-chat", { riderId });
    },
    callAssignedRider() {
      const p = this.assignedRider?.phone;
      if (p) window.location.href = `tel:${String(p).replace(/\s/g, "")}`;
    },

    bindTravelSocketListeners() {
      if (!this.$socket || this.travelRiderAcceptedHandler) return;
      this.travelRiderAcceptedHandler = (payload) => {
        if (!payload || payload.travelId !== this.currentTravelId) return;
        this.refreshTravelAndMaybeShowRider();
      };
      this.$socket.on("travel:rider_accepted", this.travelRiderAcceptedHandler);
    },

    startTravelWatch(travelId) {
      this.stopTravelWatch();
      if (!travelId) return;
      this.bindTravelSocketListeners();
      this.travelPollTimer = setInterval(() => {
        this.refreshTravelAndMaybeShowRider();
      }, 5000);
      this.refreshTravelAndMaybeShowRider();
    },

    stopTravelWatch() {
      if (this.travelPollTimer) {
        clearInterval(this.travelPollTimer);
        this.travelPollTimer = null;
      }
      if (this.$socket && this.travelRiderAcceptedHandler) {
        this.$socket.off("travel:rider_accepted", this.travelRiderAcceptedHandler);
        this.travelRiderAcceptedHandler = null;
      }
    },

    async refreshTravelAndMaybeShowRider() {
      if (!this.currentTravelId || this.currentStep !== 3) return;
      try {
        const res = await getTravelByIdApi(this.currentTravelId);
        const t = res?.data;
        if (!t) return;
        if (t.rider && ["rider_accepted", "in_progress", "completed"].includes(t.status)) {
          this.assignedRider = t.rider;
          this.currentTravel = t;
          this.riderFound = true;
          this.stopTravelWatch();
          this.loadRiderReviews(t.rider.id);
          localStorage.setItem("activeTravel", JSON.stringify({
            travelId: t.id,
            orderNo: this.orderNo,
            status: t.status,
          }));
        }
      } catch (e) {
        console.warn("refreshTravelAndMaybeShowRider:", e);
      }
    },

    async initNearbyMap() {
      if (!this.origin || !this.$refs.nearbyMapEl) return;
      await loadGoogleMapsScript();
      const center = { lat: this.origin.lat, lng: this.origin.lng };
      this.nearbyMap = new google.maps.Map(this.$refs.nearbyMapEl, {
        center,
        zoom: 15,
        minZoom: 13,
        maxZoom: 18,
        disableDefaultUI: true,
        zoomControl: true,
        zoomControlOptions: { position: google.maps.ControlPosition.RIGHT_BOTTOM },
        gestureHandling: "cooperative",
        draggable: true,
        scrollwheel: true,
        disableDoubleClickZoom: false,
        clickableIcons: false,
        styles: [
          { featureType: "poi", stylers: [{ visibility: "off" }] },
          { featureType: "transit", stylers: [{ visibility: "off" }] },
        ],
      });
      this.nearbyRadiusCircle = new google.maps.Circle({
        map: this.nearbyMap,
        center,
        radius: 2000,
        fillColor: "#2563EB",
        fillOpacity: 0.08,
        strokeColor: "#2563EB",
        strokeWeight: 2,
        strokeOpacity: 0.6,
        clickable: false,
      });
      // fit to radius circle then clamp zoom so we don't zoom out too far
      this.nearbyMap.fitBounds(this.nearbyRadiusCircle.getBounds(), 48);
      google.maps.event.addListenerOnce(this.nearbyMap, "bounds_changed", () => {
        if (this.nearbyMap && this.nearbyMap.getZoom() < 14) this.nearbyMap.setZoom(14);
      });
      const originSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="36" height="44" viewBox="0 0 36 44">
        <filter id="ds" x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" flood-color="#00000044"/>
        </filter>
        <path d="M18 2C10.268 2 4 8.268 4 16c0 10 14 26 14 26S32 26 32 16C32 8.268 25.732 2 18 2z" fill="#16a34a" filter="url(#ds)"/>
        <circle cx="18" cy="16" r="5" fill="white"/>
      </svg>`;
      this.nearbyOriginMarker = new google.maps.Marker({
        position: center,
        map: this.nearbyMap,
        icon: {
          url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(originSvg),
          scaledSize: new google.maps.Size(36, 44),
          anchor: new google.maps.Point(18, 44),
        },
        title: "จุดรับ",
      });
      await this.refreshNearbyRiders();
      this.nearbyPollTimer = setInterval(() => this.refreshNearbyRiders(), 10000);
      // re-cluster when user zooms so grouping radius adjusts
      google.maps.event.addListener(this.nearbyMap, "zoom_changed", () => this.refreshNearbyRiders());
    },

    async refreshNearbyRiders() {
      if (!this.origin || !this.nearbyMap) return;
      try {
        // อ้างอิง vehicleTypeId/serviceTypeId จาก travel ที่สร้างไว้แล้ว ไม่ใช่จาก UI selection
        const { vehicleTypeId, serviceTypeId } = this.currentTravelMeta;
        const riders = await getNearbyRiders(this.origin.lat, this.origin.lng, undefined, serviceTypeId, vehicleTypeId);
        this.nearbyRiderCount = riders.length;
        this.nearbyRiderMarkers.forEach((m) => m.setMap(null));
        this.nearbyRiderMarkers = [];

        // Cluster riders that are within ~80px of each other at current zoom
        const zoom = this.nearbyMap.getZoom() || 14;
        const clusterRadiusDeg = 0.0012 * Math.pow(2, 14 - zoom); // ~80px radius in degrees
        const clusters = [];
        riders.forEach((r) => {
          if (r.latitude == null && r.lat == null) return;
          const lat = r.latitude ?? r.lat;
          const lng = r.longitude ?? r.lng;
          const existing = clusters.find(
            (c) => Math.abs(c.lat - lat) < clusterRadiusDeg && Math.abs(c.lng - lng) < clusterRadiusDeg
          );
          if (existing) {
            existing.count++;
            existing.lat = (existing.lat * (existing.count - 1) + lat) / existing.count;
            existing.lng = (existing.lng * (existing.count - 1) + lng) / existing.count;
          } else {
            clusters.push({ lat, lng, count: 1 });
          }
        });

        clusters.forEach((c) => {
          const isSingle = c.count === 1;
          const bg = isSingle ? "white" : "#F4811F";
          const stroke = isSingle ? "#F4811F" : "white";
          const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="54" height="64" viewBox="0 0 54 64">
            <circle cx="27" cy="27" r="25" fill="${bg}" stroke="${stroke}" stroke-width="2.5" opacity="0.95"/>
            <text y="36" font-size="26" text-anchor="middle" x="27">🛵</text>
            <rect x="6" y="46" width="42" height="16" rx="8" fill="${isSingle ? '#F4811F' : '#c75f00'}"/>
            <text y="59" font-size="12" font-weight="bold" fill="white" text-anchor="middle" x="27">${c.count} คัน</text>
          </svg>`;
          const m = new google.maps.Marker({
            position: { lat: c.lat, lng: c.lng },
            map: this.nearbyMap,
            icon: {
              url: "data:image/svg+xml;charset=UTF-8," + encodeURIComponent(svg),
              scaledSize: new google.maps.Size(54, 64),
              anchor: new google.maps.Point(27, 32),
            },
            title: `${c.count} คัน`,
            zIndex: isSingle ? 1 : 10,
          });
          this.nearbyRiderMarkers.push(m);
        });
      } catch (e) {
        console.warn("refreshNearbyRiders:", e);
      }
    },

    destroyNearbyMap() {
      clearInterval(this.nearbyPollTimer);
      this.nearbyPollTimer = null;
      if (this.nearbyMap) google.maps.event.clearListeners(this.nearbyMap, "zoom_changed");
      this.nearbyRiderMarkers.forEach((m) => m.setMap(null));
      this.nearbyRiderMarkers = [];
      if (this.nearbyOriginMarker) { this.nearbyOriginMarker.setMap(null); this.nearbyOriginMarker = null; }
      if (this.nearbyRadiusCircle) { this.nearbyRadiusCircle.setMap(null); this.nearbyRadiusCircle = null; }
      this.nearbyMap = null;
      this.nearbyRiderCount = 0;
    },

    async cancelActiveTravel() {
      if (!this.currentTravelId) return;
      this.cancelling = true;
      try {
        await cancelTravelApi(this.currentTravelId, "ผู้ใช้ยกเลิก ไม่พบไรเดอร์");
        this.stopTravelWatch();
        localStorage.removeItem("activeTravel");
        this.$swal?.fire({
          icon: "success",
          title: "ยกเลิกสำเร็จ",
          text: "หากชำระผ่าน Wallet ระบบจะคืนเงินให้อัตโนมัติ",
          timer: 2500,
          showConfirmButton: false,
        });
        this.resetForm();
      } catch (error) {
        this.$swal?.fire({ icon: "error", title: "ยกเลิกไม่สำเร็จ", text: error?.response?.data?.message || error.message });
      } finally {
        this.cancelling = false;
      }
    },

    async loadRiderReviews(riderId) {
      if (!riderId) return;
      try {
        const res = await getRiderReviewsApi(riderId, { take: 1 });
        this.riderReviewAvg = res.avg || 0;
        this.riderReviewCount = res.count || 0;
      } catch (e) { /* silently fail */ }
    },

    resetForm() {
      this.stopTravelWatch();
      this.destroyNearbyMap();
      this.currentStep = 0;
      this.origin = null;
      this.originDetail = "";
      this.destination = null;
      this.destinationDetail = "";
      this.riderFound = false;
      this.assignedRider = null;
      this.parcelNote = "";
      this.errorMessage = "";
      this.currentTravelId = null;
      this.orderNo = "";
      this.activeTravel = null;
      this._resumedPrice = null;
      this.riderReviewAvg = 0;
      this.riderReviewCount = 0;
      this.currentTravel = null;
      this.selectedRiderType = this.riderTypes[0]?.id || null;
      localStorage.removeItem("activeTravel");
      if (this.directionsRenderer) {
        this.directionsRenderer.setDirections({ routes: [] });
      }
      this.$nextTick(() => this.trySetOriginFromUserLocation());
    }
  },
  beforeDestroy() {
    this.stopTravelWatch();
    this.destroyNearbyMap();
  }
};
</script>

<style scoped>
/* ── Searching screen ── */
.searching-screen {
  display: flex;
  flex-direction: column;
}

.nearby-map-wrap {
  position: relative;
  border-radius: 20px 20px 0 0;
  overflow: hidden;
  box-shadow: 0 2px 0 rgba(0,0,0,0.06);
}

.map-interaction-blocker {
  display: none;
}

.nearby-map-el {
  width: 100%;
  height: 480px;
  display: block;
}

/* floating label at top of map */
.map-top-bar {
  position: absolute;
  top: 14px;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(15, 23, 42, 0.72);
  backdrop-filter: blur(8px);
  color: white;
  font-size: 12px;
  font-weight: 600;
  padding: 5px 14px;
  border-radius: 999px;
  display: flex;
  align-items: center;
  gap: 7px;
  white-space: nowrap;
  pointer-events: none;
  letter-spacing: 0.3px;
}

.map-pulse-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4ade80;
  box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.7);
  animation: pulse-dot 1.6s ease-in-out infinite;
  flex-shrink: 0;
}

@keyframes pulse-dot {
  0%   { box-shadow: 0 0 0 0 rgba(74,222,128,0.7); }
  70%  { box-shadow: 0 0 0 7px rgba(74,222,128,0); }
  100% { box-shadow: 0 0 0 0 rgba(74,222,128,0); }
}

/* rider count badge bottom of map */
.nearby-map-badge {
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  background: linear-gradient(to top, rgba(15,23,42,0.82) 0%, transparent 100%);
  padding: 28px 20px 16px 20px;
  display: flex;
  align-items: baseline;
  gap: 6px;
  pointer-events: none;
}

.badge-count {
  font-size: 32px;
  font-weight: 900;
  color: #ffffff;
  line-height: 1;
  text-shadow: 0 2px 8px rgba(0,0,0,0.4);
}

.badge-label {
  font-size: 14px;
  font-weight: 600;
  color: rgba(255,255,255,0.85);
}

/* info card below map */
.searching-info-card {
  background: white;
  border-radius: 0 0 20px 20px;
  padding: 20px 20px 16px 20px;
  box-shadow: 0 8px 30px rgba(0,0,0,0.08);
}

.searching-title-row {
  display: flex;
  align-items: center;
  gap: 14px;
}

.searching-spinner {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  background: #F4811F;
  border-radius: 14px;
}

.spinner-ring {
  position: absolute;
  inset: -5px;
  border-radius: 18px;
  border: 3px solid #F4811F;
  opacity: 0.35;
  animation: ring-spin 1.8s linear infinite;
}

@keyframes ring-spin {
  0%   { transform: rotate(0deg) scale(1);   opacity: 0.35; }
  50%  { transform: rotate(180deg) scale(1.08); opacity: 0.15; }
  100% { transform: rotate(360deg) scale(1);  opacity: 0.35; }
}

.searching-title {
  font-size: 16px;
  font-weight: 800;
  color: #0f172a;
  line-height: 1.2;
}

.searching-sub {
  font-size: 12px;
  color: #94a3b8;
  margin-top: 2px;
}

.searching-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-top: 12px;
}

.order-ref-chip {
  display: inline-flex;
  align-items: center;
  background: #f1f5f9;
  color: #64748b;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 999px;
}

.cancel-btn {
  font-size: 12px !important;
  letter-spacing: 0 !important;
}

.call-rider-page {
  background-color: #f7f9fc;
  min-height: 100vh;
  font-family: 'Noto Sans Thai', sans-serif;
}

.rider-glass-header {
  background: linear-gradient(135deg, #0f172a 0%, #334155 100%);
  padding: 30px 0 90px 0;
  position: relative;
}

.header-icon-box {
  width: 56px;
  height: 56px;
  background: rgba(255, 255, 255, 0.15);
  backdrop-filter: blur(10px);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.1);
  box-shadow: 0 4px 15px rgba(0,0,0,0.1);
}

.header-title {
  font-size: 26px;
  font-weight: 800;
  letter-spacing: -0.5px;
}

.header-subtitle {
  font-size: 14px;
  opacity: 0.8;
}

.rider-main-container {
  margin-top: -65px;
  position: relative;
  z-index: 5;
}

/* Premium Stepper */
.stepper-wrapper {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: white;
  padding: 24px 40px;
  border-radius: 20px;
  box-shadow: 0 10px 25px rgba(0,0,0,0.04);
}

.step-node-wrapper {
  display: flex;
  align-items: center;
  flex: 1;
}

.step-node-wrapper:last-child {
  flex: none;
}

.step-node {
  display: flex;
  flex-direction: column;
  align-items: center;
  z-index: 2;
  position: relative;
}

.node-circle {
  width: 34px;
  height: 34px;
  border-radius: 50%;
  background: #f1f5f9;
  color: #64748b;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 14px;
  border: 2px solid #e2e8f0;
  transition: all 0.3s ease;
}

.step-node.active .node-circle {
  background: #F4811F;
  color: white;
  border-color: #F4811F;
  box-shadow: 0 0 0 5px rgba(244, 129, 31, 0.15);
  transform: scale(1.1);
}

.step-node.completed .node-circle {
  background: #4CAF50;
  border-color: #4CAF50;
  color: white;
}

.node-label {
  margin-top: 8px;
  font-size: 12px;
  font-weight: 700;
  color: #94a3b8;
  white-space: nowrap;
}

.step-node.active .node-label {
  color: #1e293b;
}

.node-line {
  height: 3px;
  background: #f1f5f9;
  flex: 1;
  margin: -25px 15px 0 15px;
  border-radius: 2px;
}

.node-line.filled {
  background: #4CAF50;
}

/* Content Area */
.section-title {
  font-size: 19px;
  font-weight: 800;
  color: #0f172a;
}

.location-card {
  border-radius: 24px !important;
  border: none !important;
}

.indicator-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
}
.indicator-dot.origin { background-color: #4CAF50; box-shadow: 0 0 0 4px rgba(76,175,80,0.15); }
.indicator-dot.destination { background-color: #F44336; box-shadow: 0 0 0 4px rgba(244,67,54,0.15); }

.custom-input-box {
  background: #f8fafc;
  border: 1.5px solid #e2e8f0 !important;
  border-radius: 16px !important;
  display: flex;
  align-items: center;
  transition: all 0.3s ease;
  overflow: hidden;
  max-width: 100%;
  min-width: 0;
}
.custom-input-box > .v-card__text,
.custom-input-box .row,
.custom-input-box .d-flex {
  min-width: 0;
  max-width: 100%;
}

.custom-input-box.clickable {
  cursor: pointer;
}

.custom-input-box.clickable:hover {
  border-color: #F4811F !important;
  background: white;
  box-shadow: 0 4px 15px rgba(244, 129, 31, 0.08);
}

.input-text-display {
  font-size: 15px;
  font-weight: 600;
  color: #1e293b;
  word-break: break-word;
  overflow-wrap: anywhere;
  white-space: normal;
  line-height: 1.4;
}

.input-text-display.placeholder {
  color: #94a3b8;
  font-weight: 400;
}

.detail-line {
  display: flex;
  align-items: center;
  margin-top: 6px;
  padding-top: 6px;
  border-top: 1px dashed #e5e7eb;
  font-size: 13px;
  color: #64748b;
  word-break: break-word;
  white-space: normal;
}

.summary-note {
  display: flex;
  align-items: flex-start;
  margin-top: 4px;
  font-size: 12px;
  color: #64748b;
  word-break: break-word;
}

.distance-summary-box {
  border: 1px dashed #fdba74 !important;
  border-radius: 16px;
}

.preview-map-wrapper {
  background: white;
  padding: 8px;
  border-radius: 28px;
  height: 100%;
  box-shadow: 0 10px 30px rgba(0,0,0,0.06);
}

.preview-map-container {
  height: 100%;
  min-height: 520px;
  border-radius: 22px;
  overflow: hidden;
  position: relative;
  background: #eef2f6;
}

.map-overlay-hint {
  position: absolute;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  backdrop-filter: blur(5px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  color: white;
  text-align: center;
  z-index: 10;
}

/* Rider Selection Cards */
.rider-type-card-premium {
  border-radius: 20px !important;
  border: 2px solid transparent !important;
  background: white;
  transition: all 0.3s cubic-bezier(0.2, 0.8, 0.2, 1);
  cursor: pointer;
  position: relative;
  overflow: visible !important;
}

.rider-type-card-premium:hover {
  transform: translateY(-8px);
  box-shadow: 0 15px 35px rgba(0,0,0,0.08) !important;
}

.rider-type-card-premium.selected {
  border-color: #F4811F !important;
  background: #fffcf8;
}

.rider-type-card-premium--disabled {
  opacity: 0.55;
  cursor: not-allowed !important;
  filter: grayscale(0.4);
}
.rider-type-card-premium--disabled:hover {
  transform: none !important;
  box-shadow: none !important;
}

.type-badge--disabled {
  background: #EF4444 !important;
  box-shadow: 0 4px 10px rgba(239,68,68,0.3) !important;
}

.type-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: #F4811F;
  color: white;
  padding: 4px 16px;
  border-radius: 30px;
  font-size: 11px;
  font-weight: 800;
  z-index: 10;
  box-shadow: 0 4px 10px rgba(244, 129, 31, 0.3);
}

.type-icon-wrapper {
  width: 70px;
  height: 70px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
}

.total-price-display {
  display: flex;
  align-items: baseline;
  justify-content: center;
  color: #0f172a;
}

.price-unit {
  font-size: 18px;
  font-weight: 800;
  margin-right: 4px;
}

.price-amount {
  font-size: 36px;
  font-weight: 900;
  letter-spacing: -0.5px;
}

/* Payment Section */
.payment-card {
  border-radius: 20px !important;
}

.border-bottom {
  border-bottom: 1px solid #f1f5f9;
}

/* Payment Highlight */
.payment-card .v-list-item--active {
  background-color: #FFF5EC !important;
}

.custom-selection-dot {
  width: 22px;
  height: 22px;
  border: 2px solid #cbd5e1;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
  background: white;
}

.custom-selection-dot.active {
  border-color: #F4811F;
  box-shadow: 0 0 0 4px rgba(244, 129, 31, 0.1);
}

.custom-selection-dot .dot-inner {
  width: 12px;
  height: 12px;
  background-color: #F4811F;
  border-radius: 50%;
  transform: scale(0);
  transition: transform 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275);
}

.custom-selection-dot.active .dot-inner {
  transform: scale(1);
}

.sticky-summary-card {
  position: sticky;
  top: 24px;
  border-radius: 24px !important;
  background-color: white !important;
  border: none !important;
  box-shadow: 0 10px 40px rgba(0,0,0,0.05) !important;
}

.summary-route-path {
  position: relative;
}

.path-node {
  display: flex;
  align-items: flex-start;
}

.node-icon {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  margin-top: 6px;
  margin-right: 16px;
  flex-shrink: 0;
}
.node-icon.origin { background: #4CAF50; box-shadow: 0 0 0 5px rgba(76,175,80,0.15); }
.node-icon.destination { background: #F44336; box-shadow: 0 0 0 5px rgba(244,67,54,0.15); }

.path-connector {
  width: 2px;
  height: 30px;
  border-left: 2px dashed #e2e8f0;
  margin-left: 6px;
}

.node-body .label {
  font-size: 11px;
  font-weight: 700;
  color: #94a3b8;
  text-transform: uppercase;
  margin-bottom: 2px;
}

.node-body .value {
  font-size: 14px;
  font-weight: 700;
  color: #1e293b;
}

/* Loader Premium */
.search-loader-container {
  width: 150px;
  height: 150px;
  position: relative;
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
}

.loader-ring {
  position: absolute;
  width: 100%;
  height: 100%;
  border-radius: 50%;
  border: 4px solid #F4811F;
  animation: ripple 2.5s infinite linear;
  opacity: 0;
}

.loader-ring.delay-1 {
  animation-delay: 0.8s;
}

@keyframes ripple {
  0% { transform: scale(0.4); opacity: 0; }
  30% { transform: scale(0.6); opacity: 0.8; }
  100% { transform: scale(1.8); opacity: 0; }
}

.loader-icon {
  position: relative;
  z-index: 10;
  background: white;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 10px 25px rgba(244, 129, 31, 0.2);
}

/* Final Screen — centered wrap */
.success-center-wrap {
  max-width: 520px;
  width: 100%;
}

.success-card {
  border-radius: 18px !important;
  background: white;
  box-shadow: 0 2px 16px rgba(0,0,0,0.07) !important;
}

.stats-row {
  display: flex;
  align-items: center;
  background: #f8fafc;
  border-radius: 12px;
  padding: 12px 8px;
}

.stat-item {
  flex: 1;
  text-align: center;
}

.stat-value {
  font-size: 18px;
  font-weight: 900;
  color: #0f172a;
  line-height: 1;
}

.stat-label {
  font-size: 11px;
  color: #94a3b8;
  margin-top: 3px;
}

.stat-divider {
  width: 1px;
  height: 32px;
  background: #e2e8f0;
}

/* Final Screen */
.success-status-bar {
  background: linear-gradient(135deg, #16a34a 0%, #15803d 100%);
  border-radius: 20px;
  padding: 18px 20px;
  display: flex;
  align-items: center;
  box-shadow: 0 8px 24px rgba(22, 163, 74, 0.3);
}

.success-check-circle {
  width: 52px;
  height: 52px;
  background: rgba(255,255,255,0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border: 2px solid rgba(255,255,255,0.35);
}

.trip-ref-badge {
  background: rgba(255,255,255,0.18);
  color: white;
  font-size: 12px;
  font-weight: 800;
  padding: 5px 12px;
  border-radius: 999px;
  border: 1px solid rgba(255,255,255,0.3);
  white-space: nowrap;
}

.route-summary-card {
  border-radius: 20px !important;
  background: white;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06) !important;
}

.route-dot {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  flex-shrink: 0;
}
.route-dot.origin { background: #16a34a; box-shadow: 0 0 0 4px rgba(22,163,74,0.15); }
.route-dot.destination { background: #ef4444; box-shadow: 0 0 0 4px rgba(239,68,68,0.15); }

.route-dashed-line {
  width: 2px;
  height: 20px;
  border-left: 2px dashed #e2e8f0;
  margin-left: 5px;
}

.rider-detail-card {
  border-radius: 20px !important;
  background: white;
  box-shadow: 0 4px 20px rgba(0,0,0,0.06) !important;
}

.action-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  flex: 1;
}

/* Premium Fullscreen Map Picker */
.map-dialog-container {
  position: relative;
  background-color: #f7f9fc !important;
  height: 100vh;
  width: 100vw;
  overflow: hidden;
}

.map-floating-header {
  position: absolute;
  top: 0; left: 0; right: 0;
  z-index: 10;
  background: linear-gradient(to bottom, rgba(255,255,255,0.95) 0%, rgba(255,255,255,0.8) 50%, transparent 100%);
  padding-bottom: 24px;
}

.map-fullscreen-wrapper {
  position: absolute;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 1;
}

/* Override GoogleMapPicker internal styles specifically for fullscreen picker */
.premium-google-picker ::v-deep .row {
  position: absolute !important;
  top: 75px !important; 
  left: 0 !important;
  right: 0 !important;
  z-index: 5 !important;
  margin: 0 !important;
}
.premium-google-picker ::v-deep .col-12 {
  padding: 0 20px !important;
}
.premium-google-picker ::v-deep .v-text-field {
  background: white !important;
  border-radius: 18px !important;
  box-shadow: 0 6px 20px rgba(0,0,0,0.12) !important;
}
.premium-google-picker ::v-deep .v-text-field > .v-input__control > .v-input__slot {
  box-shadow: none !important;
  border: none !important;
}
.premium-google-picker ::v-deep fieldset {
  border: none !important;
}
.premium-google-picker ::v-deep .map-container {
  border-radius: 0 !important;
  border: none !important;
}

.map-bottom-panel {
  position: absolute;
  bottom: 0; left: 0; right: 0;
  z-index: 10;
}

.shadow-md {
  box-shadow: 0 8px 25px rgba(244, 129, 31, 0.25) !important;
}

.next-action-btn {
  border-radius: 16px !important;
  font-weight: 800 !important;
  letter-spacing: 0.5px !important;
  text-transform: none !important;
}

.animate-fade-in {
  animation: slideUpFade 0.5s ease-out;
}

@keyframes slideUpFade {
  from { opacity: 0; transform: translateY(20px); }
  to { opacity: 1; transform: translateY(0); }
}

@media (max-width: 960px) {
  .preview-map-container {
    min-height: 350px;
    margin-top: 20px;
  }
  .stepper-wrapper {
    padding: 20px;
  }
  .node-line {
    margin: -25px 8px 0 8px;
  }
}
</style>
